import React, { useState } from 'react';
import { UserAvatar } from '../../ui/userAvatar';
import { MapPin, Phone, Envelope, User } from '@phosphor-icons/react';

interface PersonInfo {
    full_name: string;
    email?: string;
    job_title?: string | null;
    profile_pic_url?: string | null;
}

type ManagerType = 'primary' | 'secondary' | 'matrix';

interface ManagerInfo extends PersonInfo {
    manager_type?: ManagerType;
}

interface ProfileCardProps {
    data: {
        profile_pic_url?: string | null;
        full_name: string;
        email: string;
        job_title?: string | null;
        team?: string | null;
        department?: string | null;
        location?: string | null;
        phone_number?: string | null;
        email_verified?: boolean;
        manager?: PersonInfo | null;
        managers?: ManagerInfo[];
        primary_manager?: PersonInfo | null;
        secondary_manager?: PersonInfo | null;
        matrix_manager?: PersonInfo | null;
        reportees?: PersonInfo[];
    };
    options?: {
        show_manager?: boolean;
        show_location?: boolean;
        show_phone?: boolean;
        show_team?: boolean;
        show_tabs?: boolean;
    };
}

export const ProfileCard: React.FC<ProfileCardProps> = ({ data, options = {} }) => {
    const {
        show_location = true,
        show_phone = true,
        show_tabs = true
    } = options;

    const [activeTab, setActiveTab] = useState<'managers' | 'reportees'>('managers');

    if (!data) return <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>;

    const reporteesCount = data.reportees?.length || 0;

    // Get managers by type
    const getPrimaryManager = () => {
        if (data.primary_manager) return data.primary_manager;
        if (data.managers?.length) {
            const primary = data.managers.find(m => m.manager_type === 'primary');
            return primary || data.managers[0];
        }
        return data.manager || null;
    };

    const getSecondaryManager = () => {
        if (data.secondary_manager) return data.secondary_manager;
        if (data.managers?.length) {
            return data.managers.find(m => m.manager_type === 'secondary') ||
                (data.managers.length > 1 ? data.managers[1] : null);
        }
        return null;
    };

    const getMatrixManager = () => {
        if (data.matrix_manager) return data.matrix_manager;
        if (data.managers?.length) {
            return data.managers.find(m => m.manager_type === 'matrix') || null;
        }
        return null;
    };

    const primaryManager = getPrimaryManager();
    const secondaryManager = getSecondaryManager();
    const matrixManager = getMatrixManager();

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden">
            {/* Profile Header - New Layout */}
            <div className="p-6 pb-4">
                <div className="flex items-start gap-4">
                    <div className="ring-2 ring-indigo-100 rounded-full flex-shrink-0">
                        <UserAvatar
                            name={data.full_name}
                            size="2xl"
                            imageUrl={data.profile_pic_url || undefined}
                        />
                    </div>

                    <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-gray-900 leading-tight">
                            {data.full_name}
                        </h2>
                        <p className="text-sm text-gray-500 mb-2">
                            {data.job_title || 'No role assigned'}
                        </p>

                        <div className="flex flex-col gap-1 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                                <Envelope size={14} className="text-gray-400" />
                                <span className="truncate">{data.email}</span>
                            </div>

                            {show_phone && data.phone_number && (
                                <div className="flex items-center gap-1.5">
                                    <Phone size={14} className="text-gray-400" />
                                    <span>{data.phone_number}</span>
                                </div>
                            )}

                            {show_location && data.location && (
                                <div className="flex items-center gap-1.5">
                                    <MapPin size={14} className="text-gray-400" />
                                    <span>{data.location}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dotted Separator */}
            <div className="border-b border-dotted border-gray-300 mx-6"></div>

            {/* Managers Section - Stacked directly below header */}
            <div className="px-6 py-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                    {/* Primary Manager */}
                    <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                            Primary Manager
                        </p>
                        {primaryManager ? (
                            <p className="text-sm text-gray-900 truncate" title={primaryManager.full_name}>{primaryManager.full_name}</p>
                        ) : (
                            <p className="text-sm text-gray-400">—</p>
                        )}
                    </div>

                    {/* Secondary Manager */}
                    <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                            Secondary Manager
                        </p>
                        {secondaryManager ? (
                            <p className="text-sm text-gray-900 truncate" title={secondaryManager.full_name}>{secondaryManager.full_name}</p>
                        ) : (
                            <p className="text-sm text-gray-400">—</p>
                        )}
                    </div>
                </div>

                {/* Matrix Manager - Optional row if needed or handle 3rd item */}
                {matrixManager && (
                    <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                            Matrix Manager
                        </p>
                        <p className="text-sm text-gray-900 truncate" title={matrixManager.full_name}>{matrixManager.full_name}</p>
                    </div>
                )}
            </div>

            {/* Reportees Section at the bottom */}
            <div className="flex-1 overflow-auto px-6 pb-6 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-2 sticky top-0 bg-white pt-3 pb-1 z-10">
                    Reportees {reporteesCount > 0 && `(${reporteesCount})`}
                </p>
                <div className="space-y-2">
                    {data.reportees && data.reportees.length > 0 ? (
                        data.reportees.map((reportee, index) => (
                            <div key={index} className="flex items-center gap-2.5 py-1">
                                <UserAvatar
                                    name={reportee.full_name}
                                    size="md"
                                    imageUrl={reportee.profile_pic_url || undefined}
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-gray-900 truncate">{reportee.full_name}</p>
                                    {reportee.job_title && (
                                        <p className="text-xs text-gray-500 truncate">{reportee.job_title}</p>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="flex items-center py-2 text-sm text-gray-400">
                            <User size={16} className="mr-2" />
                            No reportees assigned
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
